import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Nav from "@/components/Nav";
import DemoShowcase from "@/components/DemoShowcase";
import {
  ArrowRight, Sparkle, CheckCircle, WhatsappLogo, Copy, PaintBrush, Star, CaretDown,
  IdentificationCard, BookOpen, Buildings, Package, TShirt, PencilLine, Flag, Certificate, Gift, Tag,
  Truck, ShieldCheck, Receipt, Storefront, GraduationCap, Heartbeat, Rocket, Cake, ForkKnife,
  ShoppingBag, ArrowUpRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SupportContactInline } from "@/components/SupportContact";

const IMG = {
  designer: "https://images.unsplash.com/photo-1558655146-9f40138edfeb?auto=format&fit=crop&w=1200&q=80",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }),
};

const CATEGORIES = [
  { icon: BookOpen, label: "Brochures", to: "/signup" },
  { icon: Flag, label: "Hoardings", to: "/signup" },
  { icon: IdentificationCard, label: "Visiting Cards", to: "/signup" },
  { icon: Package, label: "Packaging", to: "/signup" },
  { icon: TShirt, label: "T-Shirts", to: "/signup" },
  { icon: PencilLine, label: "Stationery", to: "/signup" },
  { icon: Flag, label: "Standees", to: "/signup" },
  { icon: Certificate, label: "Certificates", to: "/signup" },
  { icon: Gift, label: "Corp Gifts", to: "/signup" },
  { icon: Tag, label: "Labels", to: "/signup" },
];

const BUSINESS_TYPES = [
  { icon: Buildings, label: "Real Estate" },
  { icon: ForkKnife, label: "Restaurants & Cafes" },
  { icon: Rocket, label: "Startups" },
  { icon: ShoppingBag, label: "Retail & Fashion" },
  { icon: GraduationCap, label: "Education" },
  { icon: Heartbeat, label: "Healthcare" },
  { icon: Cake, label: "Events & Weddings" },
  { icon: Storefront, label: "E-commerce" },
];

const AI_CARDS = [
  { icon: "🎉", label: "Events", grad: "from-rose-100 to-rose-50" },
  { icon: "🏪", label: "E-commerce", grad: "from-amber-100 to-amber-50" },
  { icon: "🏢", label: "Real Estate", grad: "from-sky-100 to-sky-50" },
];

export default function Landing() {
  const [config, setConfig] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/whatsapp/config").then(r => setConfig(r.data)).catch(() => {});
  }, []);

  // Smart destination: logged-in clients skip signup, non-clients go to their dashboard.
  const briefHref = (source) => {
    if (!user) return `/signup?intent=${source}`;
    if (user.role === "client") return `/brief/new?source=${source}`;
    if (user.role === "admin") return "/admin";
    if (user.role === "designer") return "/designer";
    if (user.role === "vendor") return "/vendor";
    return "/dashboard";
  };

  const productHref = () => {
    if (!user) return "/signup";
    if (user.role === "client") return "/brief/new";
    if (user.role === "admin") return "/admin";
    if (user.role === "designer") return "/designer";
    if (user.role === "vendor") return "/vendor";
    return "/dashboard";
  };

  const submitPrompt = (e) => {
    e?.preventDefault();
    if (!prompt.trim()) { toast.error("Describe your brand first"); return; }
    localStorage.setItem("pf_prompt_seed", prompt.trim());
    nav(briefHref("ai"));
  };

  const wappUrl = config
    ? `https://wa.me/${config.sandbox_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(config.join_code)}`
    : "#";

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      {/* ============ HERO — AI + WHATSAPP SIDE BY SIDE ============ */}
      <section className="border-b border-[var(--pf-border)] relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-8 py-14 md:py-16 items-stretch">
          {/* LEFT — Describe your brand */}
          <div className="lg:col-span-7 flex flex-col justify-center relative z-10">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] text-xs font-bold rounded-full uppercase tracking-wider w-fit">
              <Sparkle size={14} weight="fill" /> Free AI Logo Generator
            </motion.div>
            <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1}
              className="mt-6 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] text-[var(--pf-text)]">
              A logo in 60 seconds.<br/>
              Then <span className="text-[var(--pf-brand)]">everything else</span> your brand needs.
            </motion.h1>
            <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2}
              className="mt-5 text-lg text-[var(--pf-text-2)] max-w-xl">
              AI-powered branding & print for Indian small businesses. Start with a free logo,
              then design menus, business cards, packaging, signage — printed and delivered.
            </motion.p>

            {/* Primary CTA: free logo generator */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={2.5}
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <Link to="/logo-studio" data-testid="hero-logo-cta"
                className="inline-flex items-center gap-2 bg-[var(--pf-brand)] hover:bg-[var(--pf-text)] text-white px-6 py-3.5 font-bold rounded-lg transition-colors">
                <Sparkle size={16} weight="fill"/> Generate my free logo →
              </Link>
              <Link to="/brief/new" data-testid="hero-brief-cta"
                className="inline-flex items-center gap-2 border-2 border-[var(--pf-text)] hover:bg-[var(--pf-text)] hover:text-white text-[var(--pf-text)] px-6 py-3.5 font-semibold rounded-lg transition-colors">
                Design a print piece
              </Link>
            </motion.div>

            {/* WhatsApp CTA — solid green button, below primary CTAs */}
            <motion.a href={wappUrl} target="_blank" rel="noopener noreferrer"
              initial="hidden" animate="visible" variants={fadeUp} custom={3}
              data-testid="wa-open-chat"
              className="mt-5 inline-flex items-center gap-2.5 bg-[#128C4A] hover:bg-[#0F7C41] text-white pl-2 pr-4 py-2 rounded-full text-sm font-bold shadow-md hover:shadow-lg transition-all group w-fit">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
                <WhatsappLogo size={18} weight="fill" className="text-white"/>
              </span>
              Prefer WhatsApp? Order in a chat
              <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform"/>
            </motion.a>

            {/* Prompt form — demoted to secondary AI-print entry point */}
            <motion.form onSubmit={submitPrompt} initial="hidden" animate="visible" variants={fadeUp} custom={3.5}
              className="mt-6 max-w-2xl">
              <div className="pf-label mb-2 text-[var(--pf-text-3)]">Or, if you already know what you need:</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text" value={prompt} onChange={(e)=>setPrompt(e.target.value)}
                  placeholder="e.g. Luxury real estate brochure for Green Valley, Chennai"
                  data-testid="hero-prompt"
                  className="flex-1 bg-white border border-[var(--pf-border)] hover:border-[var(--pf-text)] focus:border-[var(--pf-brand)] focus:outline-none focus:ring-4 focus:ring-[var(--pf-brand)]/10 px-4 py-3 rounded-lg text-[var(--pf-text)] transition-all"
                />
                <button type="submit" data-testid="hero-generate"
                  className="inline-flex items-center gap-2 bg-white hover:bg-[var(--pf-surface-2)] text-[var(--pf-text)] border-2 border-[var(--pf-border)] hover:border-[var(--pf-text)] px-6 py-3 font-semibold rounded-lg transition-colors whitespace-nowrap">
                  <ArrowRight size={16}/> Go
                </button>
              </div>
            </motion.form>

            {/* WhatsApp CTA moved above; nothing here */}

            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4}
              className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--pf-text-2)]">
              {["Print-ready PDF output", "3 concepts in 2 min", "No design skills needed"].map(x => (
                <div key={x} className="flex items-center gap-1.5">
                  <CheckCircle size={16} weight="fill" className="text-emerald-500" /> {x}
                </div>
              ))}
            </motion.div>

            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={5}
              className="mt-10 pt-8 border-t border-[var(--pf-border)] flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[var(--pf-text-3)] font-mono uppercase tracking-widest">
              <span>Trusted by</span>
              {["NORTHWIND", "SOLARIS", "KAVA CO.", "MERIDIAN", "BLUM & CO"].map(b => (
                <span key={b} className="font-black text-[var(--pf-text-2)]">{b}</span>
              ))}
            </motion.div>
          </div>

          {/* RIGHT — For Startups (split-hero) */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative" data-testid="hero-startup">
            <div className="relative rounded-2xl overflow-hidden shadow-xl h-full flex flex-col"
                 style={{
                   background: "linear-gradient(155deg, #1F1917 0%, #241C19 55%, #2A211D 100%)",
                   color: "#F5EDE7",
                   border: "1px solid #2E2723",
                 }}>
              <div className="pf-cmyk-bar"><span/><span/><span/><span/></div>
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--pf-brand)]/15 text-[var(--pf-brand)] text-[10px] font-bold rounded-full uppercase tracking-wider self-start">
                  <Rocket size={12} weight="fill"/> Starting a business?
                </div>
                <h2 className="mt-4 text-2xl md:text-4xl font-black tracking-tighter leading-[1.02]">
                  From logo to launch. <br/>
                  <span className="text-[var(--pf-brand)]">Everything your new brand needs.</span>
                </h2>
                <p className="mt-3 text-sm md:text-[15px] leading-relaxed" style={{ color: "#C9BEB7" }}>
                  Logo · Brand identity · Print collateral · Signage · Brand video — designed by AI,
                  delivered by our vetted partners. Launch in 7 days.
                </p>

                {/* 5 service tiles */}
                <div className="mt-6 grid grid-cols-5 gap-2">
                  {[
                    { i: Sparkle,    l: "Logo" },
                    { i: PaintBrush, l: "Brand" },
                    { i: BookOpen,   l: "Print" },
                    { i: Flag,       l: "Signage" },
                    { i: Rocket,     l: "Video" },
                  ].map(({ i: Ic, l }) => (
                    <div key={l}
                      className="transition-colors rounded-lg p-2.5 flex flex-col items-center gap-1.5"
                      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                      <Ic size={20} weight="duotone" className="text-[var(--pf-brand)]" />
                      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#E6DDD5" }}>{l}</div>
                    </div>
                  ))}
                </div>

                {/* Example story */}
                <div className="mt-6 p-4 border-l-2 border-[var(--pf-brand)] rounded-r-md"
                     style={{ background: "rgba(255,255,255,0.03)" }}>
                  <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#9E938B" }}>Example — opening a coffee shop?</div>
                  <p className="text-xs leading-relaxed" style={{ color: "#D8CFC8" }}>
                    We design your logo → menu cards → shop signage → business cards → uniforms →
                    a 15-sec brand reel for Instagram. All coordinated. All printed. All delivered.
                  </p>
                </div>

                <div className="mt-auto pt-6 flex flex-col sm:flex-row gap-3">
                  <Link to="/startup-kit"
                    data-testid="startup-cta"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[var(--pf-brand)] hover:bg-white hover:text-[#1F1917] text-white px-5 py-3 font-bold rounded-lg transition-colors">
                    Launch my brand <ArrowRight size={14}/>
                  </Link>
                  <Link to="/startup-kit#pricing"
                    data-testid="startup-pricing"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold rounded-lg transition-colors"
                    style={{ border: "1px solid rgba(255,255,255,0.25)", color: "#F5EDE7" }}>
                    See pricing
                  </Link>
                </div>
                {/* Contact-us shortcut under the CTAs — same 3-channel block as /startup-kit */}
                <div className="mt-4">
                  <SupportContactInline variant="hero" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Feature strip */}
        <div className="border-t border-[var(--pf-border)] bg-white">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 grid grid-cols-2 md:grid-cols-4 gap-0">
            {[
              { i: Truck, t: "Same-day delivery", d: "Chennai, Bangalore, Hyderabad" },
              { i: Sparkle, t: "AI-generated designs", d: "3 concepts in under 2 minutes" },
              { i: ShieldCheck, t: "Print-ready output", d: "300 DPI, CMYK, bleed included" },
              { i: Receipt, t: "GST invoices", d: "Automatic on every order" },
            ].map((f, i) => (
              <div key={f.t} className={`flex items-center gap-4 py-5 px-2 ${(i < 3) ? "md:border-r border-[var(--pf-border)]" : ""} ${i === 1 ? "border-l md:border-l-0" : ""}`} data-testid={`feature-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] flex items-center justify-center shrink-0">
                  <f.i size={22} weight="duotone" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{f.t}</div>
                  <div className="text-xs text-[var(--pf-text-2)] mt-0.5">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ POPULAR CATEGORIES ============ */}
      <section className="border-b border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight">Popular categories</h2>
            <Link to={productHref()} className="text-sm font-semibold text-[var(--pf-brand)] hover:underline flex items-center gap-1" data-testid="view-all-cats">
              View all <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-10 gap-3 md:gap-4" data-testid="popular-categories">
            {CATEGORIES.map((c, i) => (
              <motion.div key={c.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <Link to={productHref()} data-testid={`cat-${c.label.toLowerCase().replace(/\s/g,'-')}`}
                  className="group flex flex-col items-center justify-center gap-3 aspect-square bg-white border border-[var(--pf-border)] rounded-xl p-3 hover:border-[var(--pf-brand)] hover:shadow-md transition-all">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[var(--pf-brand)]/5 text-[var(--pf-brand)] group-hover:bg-[var(--pf-brand)] group-hover:text-white transition-colors">
                    <c.icon size={20} weight="duotone" />
                  </div>
                  <div className="text-xs font-semibold text-center leading-tight text-[var(--pf-text)]">{c.label}</div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SHOP BY BUSINESS TYPE ============ */}
      <section className="border-b border-[var(--pf-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="flex items-end justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight">Shop by business type</h2>
            <Link to={productHref()} className="text-sm font-semibold text-[var(--pf-brand)] hover:underline flex items-center gap-1" data-testid="view-all-biz">
              View all <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="business-types">
            {BUSINESS_TYPES.map((b, i) => (
              <motion.div key={b.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <Link to={productHref()} data-testid={`biz-${b.label.toLowerCase().replace(/[\s&]+/g,'-')}`}
                  className="group flex items-center gap-3 bg-[var(--pf-bg)] hover:bg-white border border-[var(--pf-border)] hover:border-[var(--pf-brand)] hover:shadow-md rounded-xl p-4 transition-all">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-[var(--pf-border)] text-[var(--pf-brand)] group-hover:bg-[var(--pf-brand)] group-hover:text-white transition-colors">
                    <b.icon size={20} weight="duotone" />
                  </div>
                  <div className="text-sm font-semibold text-[var(--pf-text)]">{b.label}</div>
                  <ArrowUpRight size={14} className="ml-auto text-[var(--pf-text-3)] group-hover:text-[var(--pf-brand)] transition-colors" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AI VS HUMAN ============ */}
      <section className="border-b border-[var(--pf-border)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] text-xs font-bold rounded-full uppercase tracking-wider mb-4">Two paths</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] max-w-3xl mx-auto">
              Fast AI concept, or crafted by a human. <span className="text-[var(--pf-brand)]">You choose.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="ai-vs-human">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}
              className="rounded-2xl border border-[var(--pf-border)] bg-white p-8 md:p-10 flex flex-col hover:shadow-lg hover:border-[var(--pf-text)] transition-all">
              <div className="w-14 h-14 rounded-xl bg-[var(--pf-text)] text-white flex items-center justify-center mb-6">
                <Sparkle size={26} weight="fill" />
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-[var(--pf-text-3)] mb-2">Fast track</div>
              <h3 className="text-2xl md:text-3xl font-black tracking-tight">Generate with AI</h3>
              <p className="mt-4 text-[var(--pf-text-2)]">
                Describe your design in a few lines. Our Nano-Banana AI generates print-ready concepts in under 60 seconds. Free unlimited iterations.
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                {["Under 60 seconds", "Unlimited free iterations", "Best for concept exploration", "Cost: ₹0 for the design"].map(x => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 shrink-0"/> {x}</li>
                ))}
              </ul>
              <Link to={briefHref("ai")} className="mt-8 self-start inline-flex items-center gap-2 bg-[var(--pf-text)] hover:bg-[var(--pf-brand)] text-white px-6 py-3 font-semibold rounded-lg transition-colors" data-testid="cta-ai">
                Try AI design <ArrowRight size={14}/>
              </Link>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}
              className="relative rounded-2xl border-2 border-[var(--pf-brand)] bg-gradient-to-br from-[var(--pf-brand)]/5 to-white p-8 md:p-10 flex flex-col hover:shadow-lg transition-all">
              <div className="absolute -top-3 left-8 bg-[var(--pf-yellow)] text-black px-3 py-1 text-[10px] uppercase tracking-widest font-black rounded">POPULAR</div>
              <div className="w-14 h-14 rounded-xl bg-[var(--pf-brand)] text-white flex items-center justify-center mb-6">
                <PaintBrush size={26} weight="duotone" />
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-[var(--pf-text-3)] mb-2">Crafted path</div>
              <h3 className="text-2xl md:text-3xl font-black tracking-tight">Hire a designer</h3>
              <p className="mt-4 text-[var(--pf-text-2)]">
                Pick from our roster of vetted Indian designers. They deliver print-ready 300 DPI CMYK files with your brand tokens, in 24–48 hours.
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                {["24–48 hour turnaround", "Print-ready 300 DPI CMYK PDF", "Direct chat with your designer", "From ₹500 per job"].map(x => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[var(--pf-brand)] mt-0.5 shrink-0"/> {x}</li>
                ))}
              </ul>
              <Link to={briefHref("human")} className="mt-8 self-start inline-flex items-center gap-2 bg-[var(--pf-brand)] hover:bg-[var(--pf-text)] text-white px-6 py-3 font-semibold rounded-lg transition-colors" data-testid="cta-human">
                Hire a designer <ArrowRight size={14}/>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ LIVE DEMO ============ */}
      <DemoShowcase />

      {/* ============ DESIGNER MARKETPLACE ============ */}
      <section className="border-b border-[var(--pf-border)] bg-[var(--pf-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] text-xs font-bold rounded-full uppercase tracking-wider mb-3">Marketplace</div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-black tracking-tighter">Vetted Indian designers</h2>
            </div>
            <span className="text-sm font-semibold text-[var(--pf-text-3)]" title="Designers are invite-only — email hello@talaio.com to apply">Designers · invite-only</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { name:"Priya Sharma", city:"Mumbai · D2C specialist", rate:"₹1,500 / job", tags:["Cards","Packaging"], accent:"from-rose-100 to-rose-50" },
              { name:"Arun Iyer", city:"Bangalore · Tech branding", rate:"₹2,200 / job", tags:["Posters","Signage"], accent:"from-sky-100 to-sky-50" },
              { name:"Sana Khan", city:"Delhi · FMCG & Retail", rate:"₹1,800 / job", tags:["Labels","Packaging"], accent:"from-amber-100 to-amber-50" },
            ].map((d, i) => (
              <motion.div key={d.name} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="rounded-2xl border border-[var(--pf-border)] bg-white p-5 hover:border-[var(--pf-brand)] hover:shadow-md transition-all">
                <div className={`w-full aspect-[4/3] rounded-xl bg-gradient-to-br ${d.accent} border border-[var(--pf-border)] relative overflow-hidden`}>
                  <div className="absolute inset-4 border border-black/10 rounded-lg"></div>
                  <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                    <div className="font-black text-2xl text-black tracking-tighter">{d.name.split(" ").map(w=>w[0]).join("")}</div>
                    <div className="text-[9px] font-mono font-bold text-black/70">12+ PROJECTS</div>
                  </div>
                </div>
                <div className="mt-4 flex items-start justify-between">
                  <div>
                    <div className="font-bold">{d.name}</div>
                    <div className="text-xs text-[var(--pf-text-2)] mt-0.5">{d.city}</div>
                  </div>
                  <div className="text-xs flex items-center gap-1 font-mono font-bold">
                    <Star size={12} weight="fill" className="text-[var(--pf-yellow)]" /> 4.9
                  </div>
                </div>
                <div className="flex gap-1.5 mt-3">
                  {d.tags.map(t => <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[var(--pf-bg)] rounded">{t}</span>)}
                </div>
                <div className="mt-4 pt-4 border-t border-[var(--pf-border)] flex items-center justify-between">
                  <div className="font-mono font-bold text-sm">{d.rate}</div>
                  <div className="text-xs font-bold text-[var(--pf-brand)] flex items-center gap-1">Hire <ArrowUpRight size={12}/></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="border-b border-[var(--pf-border)] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] text-xs font-bold rounded-full uppercase tracking-wider mb-3">Loved by</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter">What our clients say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { q:"Went from 3-week design cycles to 1-day. Our agency handles 4× more clients with the same team.", n:"Meera Patel", r:"Founder · Ink&Paper Studio, Mumbai" },
              { q:"WhatsApp ordering unlocked 40% of clients who never opened the web app. Reorders through the roof.", n:"Rohan Deshmukh", r:"COO · KavaCo D2C, Pune" },
            ].map((t, i) => (
              <motion.div key={t.n} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="rounded-2xl border border-[var(--pf-border)] bg-[var(--pf-bg)] p-8 md:p-10">
                <div className="flex gap-1 mb-5">
                  {Array.from({length:5}).map((_,i) => <Star key={i} size={16} weight="fill" className="text-[var(--pf-yellow)]" />)}
                </div>
                <p className="text-lg md:text-xl font-medium leading-snug tracking-tight">"{t.q}"</p>
                <div className="mt-6 pt-5 border-t border-[var(--pf-border)]">
                  <div className="font-bold">{t.n}</div>
                  <div className="text-xs text-[var(--pf-text-2)] mt-0.5">{t.r}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="border-b border-[var(--pf-border)]">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] text-xs font-bold rounded-full uppercase tracking-wider mb-3">FAQ</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter">Quick answers</h2>
          </div>
          <div className="space-y-3" data-testid="faq">
            {[
              { q:"Is the AI-generated design print-ready?", a:"It's a concept preview. Once you approve, our design ops team prepares the print-ready 300 DPI CMYK file with proper bleed. Or hire a human designer for direct print-ready delivery." },
              { q:"How do I pay?", a:"Currently invoice-based with GST-compliant invoicing. Razorpay checkout is coming soon." },
              { q:"Where do you deliver?", a:"Anywhere in India via our vendor network. Metro cities in 3-4 days, Tier 2/3 in 5-7 days." },
              { q:"Can I use my own designer?", a:"Yes — Agency plans allow you to bring your own designer roster into your dashboard." },
              { q:"Do you handle GST invoicing?", a:"Every order comes with a GST-compliant invoice auto-generated on approval." },
            ].map((f, i) => (
              <div key={f.q} className={`rounded-xl border transition-all ${openFaq === i ? "border-[var(--pf-brand)] bg-white shadow-md" : "border-[var(--pf-border)] bg-white"}`}>
                <button onClick={()=>setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between gap-4"
                  data-testid={`faq-${i}`}>
                  <span className="font-semibold">{f.q}</span>
                  <CaretDown size={18} className={`transition-transform text-[var(--pf-text-3)] ${openFaq === i ? "rotate-180" : ""}`}/>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-[var(--pf-text-2)]">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOOTER CTA ============ */}
      <section className="bg-[var(--pf-text)] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 text-white/70 text-xs font-bold rounded-full uppercase tracking-wider mb-4">Ready?</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
              Your next print job. <span className="text-[var(--pf-yellow)]">Done in a chat.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to={user ? productHref() : "/signup"} className="inline-flex items-center gap-2 bg-white text-black hover:bg-[var(--pf-yellow)] px-6 py-4 font-semibold rounded-lg justify-center transition-colors" data-testid="footer-cta-primary">
              {user ? "Start a brief" : "Start free"} <ArrowRight size={14}/>
            </Link>
            <a href={wappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-[#128C4A] text-white hover:bg-[#128C4A] hover:text-white px-6 py-4 font-semibold rounded-lg justify-center transition-all" data-testid="footer-cta-wa">
              <WhatsappLogo size={16} weight="fill"/> Try WhatsApp bot
            </a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2 md:col-span-2">
              <div className="text-2xl font-black tracking-tighter">TALAIO<span className="text-[var(--pf-brand)]">/AI</span></div>
              <div className="mt-3 text-xs uppercase tracking-widest font-bold text-white/50">Made for India · Swiss precision</div>
              <div className="pf-cmyk-bar mt-6 max-w-[220px]"><span/><span/><span/><span/></div>
            </div>
            {[
              { h:"Product", l:["AI Design","Human Designers","Vendor Network","WhatsApp Bot"] },
              { h:"Company", l:["About","Careers","Contact","Blog"] },
              { h:"Legal", l:["Terms","Privacy","GST Policy","Refunds"] },
            ].map(col => (
              <div key={col.h}>
                <div className="text-xs uppercase tracking-widest font-bold text-white/50 mb-4">{col.h}</div>
                <ul className="space-y-2 text-sm">
                  {col.l.map(x => <li key={x} className="text-white/80 hover:text-white transition-colors cursor-pointer">{x}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/50">
              <div>© 2026 Talaio/AI · All rights reserved</div>
              <div className="font-mono">CMYK · 300 DPI · MADE IN INDIA</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Bubble({ side, media, children }) {
  const isIn = side === "in";
  return (
    <div className={`flex ${isIn ? "" : "justify-end"}`}>
      <div className={`max-w-[85%] px-2.5 py-1.5 text-[11px] leading-snug ${isIn ? "bg-white/10 text-white rounded-r-lg rounded-tl-lg" : "bg-[#25D366] text-black rounded-l-lg rounded-tr-lg"} ${media ? "p-2" : ""}`}>
        {children}
      </div>
    </div>
  );
}
